/**
 * Day/night cycle.
 *
 * Kate was precise about the length and corrected herself to land on it:
 * "Days last in this game, 25 minutes. Day plus night. So if you— no, it's actually
 * 26 minutes. So, um, 13 minutes is day and 13 minutes is night."
 *
 * So: 1560 seconds total, split exactly in half. Sleeping (M3) and the 1-in-game-day
 * power-up duration (M5) both measure against this clock.
 */
import * as THREE from 'three'

export const DAY_SECONDS = 13 * 60
export const NIGHT_SECONDS = 13 * 60
export const CYCLE_SECONDS = DAY_SECONDS + NIGHT_SECONDS

export type Phase = 'day' | 'night'

/** How far the sun tilts off vertical at noon — a tropical, near-overhead sun. */
const SUN_TILT = 0.35
const SUN_DISTANCE = 80

const DAY_SKY = new THREE.Color(0x8fb7d4)
const NIGHT_SKY = new THREE.Color(0x21314e)
const DAY_SUN = new THREE.Color(0xfff2e0)
const DUSK_SUN = new THREE.Color(0xff9d5c)
const MOONLIGHT = new THREE.Color(0xa8bcea)

const DAY_GROUND_BOUNCE = new THREE.Color(0x7d9660)
const NIGHT_GROUND_BOUNCE = new THREE.Color(0x5a6c91)

export class DayNightCycle {
  /** Seconds into the current 26-minute cycle. */
  time = 0

  private readonly scratch = new THREE.Color()

  /** Start at mid-morning rather than pitch dark, so a fresh game opens in daylight. */
  constructor(startSeconds = DAY_SECONDS * 0.35) {
    this.time = startSeconds % CYCLE_SECONDS
  }

  advance(dt: number): void {
    this.time = (this.time + dt) % CYCLE_SECONDS
  }

  get phase(): Phase {
    return this.time < DAY_SECONDS ? 'day' : 'night'
  }

  /** 0 at the start of the current phase, 1 at its end. */
  get phaseProgress(): number {
    return this.phase === 'day'
      ? this.time / DAY_SECONDS
      : (this.time - DAY_SECONDS) / NIGHT_SECONDS
  }

  /** Whole cycle as 0..1, where 0 is sunrise and 0.5 is sunset. */
  get cycleProgress(): number {
    return this.time / CYCLE_SECONDS
  }

  /**
   * Sun elevation in radians: 0 at the horizon, +PI/2 overhead, negative below.
   * Sunrise at cycle 0, noon at 0.25, sunset at 0.5, midnight at 0.75 — so the sun is
   * above the horizon for exactly the 13 minutes `phase` calls day.
   */
  get sunElevation(): number {
    return Math.sin(this.cycleProgress * Math.PI * 2) * (Math.PI / 2)
  }

  /** 1 in full daylight, 0 in full night, easing smoothly across dawn and dusk. */
  get daylight(): number {
    // The 2.2 gain made this almost a square wave: full day to full night inside two
    // minutes, with only three of the cycle's twenty-six spent in between. Sunset is
    // the best-looking moment a day/night cycle has, and Kate's whole "get to your
    // burrow before dark" loop depends on watching it coming. A gentler gain plus a
    // smoothstep gives a real dawn and dusk without shortening either phase.
    const raw = THREE.MathUtils.clamp(Math.sin(this.sunElevation) * 0.85 + 0.42, 0, 1)
    return raw * raw * (3 - 2 * raw)
  }

  /** Drive the scene's lighting and sky from the current time. */
  apply(scene: THREE.Scene, sun: THREE.DirectionalLight, sky: THREE.HemisphereLight,
        moon?: THREE.DirectionalLight): void {
    const elevation = this.sunElevation
    const azimuth = this.cycleProgress * Math.PI * 2
    sun.position.set(
      Math.cos(azimuth) * Math.cos(elevation) * SUN_DISTANCE,
      Math.sin(elevation) * SUN_DISTANCE,
      Math.sin(azimuth) * Math.cos(elevation) * SUN_DISTANCE + SUN_TILT * SUN_DISTANCE,
    )

    const light = this.daylight

    // Warm the sun toward orange as it nears the horizon, then hand over to moonlight.
    const horizonCloseness = 1 - THREE.MathUtils.clamp(Math.sin(Math.max(elevation, 0)) * 3, 0, 1)
    this.scratch.copy(DAY_SUN).lerp(DUSK_SUN, horizonCloseness)
    if (light < 0.15) this.scratch.lerp(MOONLIGHT, 1 - light / 0.15)
    sun.color.copy(this.scratch)
    // Never fully dark: Kate's night is playable, and the moon is the reason.
    sun.intensity = 0.32 + light * 2.7

    // Moonlight comes from the opposite side of the sky and fades in as the sun leaves.
    if (moon) {
      moon.position.copy(sun.position).multiplyScalar(-1)
      const night = Math.max(0, 1 - light * 1.6)
      // Moonlight is a fill, not a spotlight. Driven hard it blew every moon-facing
      // face to near-white while everything turned to it edge-on stayed black, which
      // reads as broken rather than as night. Dimmer light plus a raised sky floor
      // (below) keeps shapes legible everywhere instead of only where the moon hits.
      moon.intensity = night * 1.6
      moon.visible = night > 0.01
    }

    this.scratch.copy(NIGHT_SKY).lerp(DAY_SKY, light)
    if (scene.background instanceof THREE.Color) scene.background.copy(this.scratch)
    if (scene.fog) scene.fog.color.copy(this.scratch)

    // Generous indirect light: a three-blob canopy blocks almost all direct sun,
    // and a jungle floor you cannot see is not what "looks like a real jungle" means.
    // The floor is raised at night rather than at noon: with no sun, indirect sky light
    // is all that fills the shadows, and a jungle you cannot see is not playable.
    sky.intensity = 1.9 + light * 1.4 + Math.max(0, 1 - light * 1.6) * 1.25
    sky.color.copy(this.scratch)
    this.scratch.copy(NIGHT_GROUND_BOUNCE).lerp(DAY_GROUND_BOUNCE, light)
    sky.groundColor.copy(this.scratch)
  }

  /** "13:02 day" style label for the HUD. */
  get clockLabel(): string {
    const remaining = this.phase === 'day' ? DAY_SECONDS - this.time : CYCLE_SECONDS - this.time
    const minutes = Math.floor(remaining / 60)
    const seconds = Math.floor(remaining % 60)
    return `${this.phase} ${minutes}:${seconds.toString().padStart(2, '0')} left`
  }
}
